/*
 * Small homepage interactions and optional motion bootstrap.
 * Native Shopify navigation, filters, galleries and forms need none of this code.
 */
(() => {
  const bootstrap = document.currentScript;
  if (window.__fmPureInteractions) return;
  window.__fmPureInteractions = true;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const guides = new Map();
  const canAnimateGuide = () =>
    !motionPreference.matches &&
    !connection?.saveData &&
    !window.Shopify?.designMode &&
    !document.hidden;

  const stopGuideAnimations = () => {
    for (const state of guides.values()) {
      state.animations.forEach((animation) => animation.cancel());
      state.animations = [];
    }
  };

  // This is a useful interaction, not a motion dependency. It also works when
  // animations are disabled, the theme editor is open, or the bundle fails.
  const setupGuides = (root = document) => {
    const sections = [...root.querySelectorAll('.fm-pure-scent-guide')];
    if (root.matches?.('.fm-pure-scent-guide')) sections.unshift(root);
    for (const section of sections) {
      if (guides.has(section)) continue;
      const images = [...section.querySelectorAll('img[data-fm-scent-image]')];
      const details = [...section.querySelectorAll('details[data-fm-scent-index]')];
      if (!images.length || !details.length) continue;
      const state = { animations: [], remove: [], active: undefined };
      const activate = (index, animate = false) => {
        const next = images.find((image) => image.dataset.fmScentImage === index);
        if (!next || next === state.active) return;
        state.animations.forEach((animation) => animation.cancel());
        state.animations = [];
        const previous = state.active;
        for (const image of images) {
          image.dataset.active = image === next ? 'true' : 'false';
          // Removing the inactive override respects the original alt semantics:
          // alt="" remains decorative instead of being forced into the tree.
          if (image === next) image.removeAttribute('aria-hidden');
          else image.setAttribute('aria-hidden', 'true');
        }
        state.active = next;
        if (!animate || !canAnimateGuide() || typeof next.animate !== 'function') return;
        const timing = { duration: 380, easing: 'cubic-bezier(.2,.7,.2,1)' };
        if (previous) {
          state.animations.push(previous.animate([{ opacity: 1 }, { opacity: 0 }], timing));
        }
        state.animations.push(next.animate(
          [
            { opacity: 0, transform: 'translate3d(0,12px,0) scale(1.025)' },
            { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
          ],
          timing,
        ));
      };
      for (const detail of details) {
        const onToggle = () => {
          if (detail.open) activate(detail.dataset.fmScentIndex, true);
        };
        detail.addEventListener('toggle', onToggle);
        state.remove.push(() => detail.removeEventListener('toggle', onToggle));
      }
      section.dataset.fmGuideEnhanced = 'true';
      section.classList.add('fm-pure-guide-enhanced');
      guides.set(section, state);
      activate(
        details.find((detail) => detail.open)?.dataset.fmScentIndex ??
        images.find((image) => image.dataset.active === 'true')?.dataset.fmScentImage ??
        images[0].dataset.fmScentImage,
      );
    }
  };
  const removeGuides = (root) => {
    for (const [section, state] of guides) {
      if (root !== section && !root.contains?.(section)) continue;
      state.animations.forEach((animation) => animation.cancel());
      state.remove.forEach((remove) => remove());
      guides.delete(section);
    }
  };
  setupGuides();
  motionPreference.addEventListener('change', stopGuideAnimations);
  connection?.addEventListener?.('change', stopGuideAnimations);
  document.addEventListener('shopify:section:unload', (event) => removeGuides(event.target));
  document.addEventListener('shopify:section:load', (event) => setupGuides(event.target));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopGuideAnimations();
  });
  window.addEventListener('pagehide', stopGuideAnimations);
  if (!bootstrap?.dataset.motionSrc || !document.querySelector('.fm-pure-hero')) return;

  let source;
  try {
    source = new URL(bootstrap.dataset.motionSrc, window.location.href);
    if (source.protocol !== 'https:' && !(source.protocol === 'http:' && source.origin === window.location.origin)) return;
  } catch {
    return;
  }

  let idleId;
  let timerId;
  let loading;
  let failed = false;
  let suspended = false;
  let pageLoaded = document.readyState === 'complete';

  const allowed = () =>
    !motionPreference.matches &&
    !connection?.saveData &&
    !window.Shopify?.designMode &&
    !document.hidden &&
    !suspended &&
    !failed &&
    Boolean(document.querySelector('.fm-pure-hero'));

  const cancelPending = () => {
    if (idleId !== undefined) window.cancelIdleCallback?.(idleId);
    if (timerId !== undefined) window.clearTimeout(timerId);
    idleId = undefined;
    timerId = undefined;
  };

  const stop = () => {
    cancelPending();
    window.FMPureMotion?.stop();
  };

  const load = () => {
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = source.href;
        script.async = true;
        script.dataset.fmMotionBundle = '';
        script.onload = () => {
          if (window.FMPureMotion) resolve(window.FMPureMotion);
          else reject(new Error('Pure motion unavailable'));
        };
        script.onerror = () => reject(new Error('Pure motion unavailable'));
        document.head.appendChild(script);
      });
    }
    return loading;
  };

  const enhance = () => {
    idleId = undefined;
    timerId = undefined;
    if (!allowed()) return;
    load()
      .then((motion) => {
        if (allowed()) motion.start(document);
        else motion.stop();
      })
      .catch(() => {
        failed = true;
        stop();
      });
  };

  const schedule = () => {
    cancelPending();
    if (!pageLoaded || !allowed()) return;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(enhance, { timeout: 2000 });
    } else {
      timerId = window.setTimeout(enhance, 200);
    }
  };

  const onPreferenceChange = () => {
    if (allowed()) schedule();
    else stop();
  };

  motionPreference.addEventListener('change', onPreferenceChange);
  connection?.addEventListener?.('change', onPreferenceChange);
  window.addEventListener('load', () => {
    pageLoaded = true;
    schedule();
  }, { once: true });
  window.addEventListener('pagehide', () => {
    suspended = true;
    stop();
  });
  window.addEventListener('pageshow', () => {
    suspended = false;
    schedule();
  });
  // Editor sections are always static. Cleanup also covers a mode transition.
  document.addEventListener('shopify:section:unload', stop);
  document.addEventListener('shopify:section:load', () => {
    stop();
    if (!window.Shopify?.designMode) schedule();
  });
  document.addEventListener('visibilitychange', onPreferenceChange);
  schedule();
})();
